import { useCallback, useEffect, useState } from 'react';
import type { ObjectiveSummaryView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, Plus, Target } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { CollapsibleGroup } from '@ui/shared/primitives/CollapsibleGroup';
import { formatShortDate } from '@ui/shared/format/date';

/**
 * All Objectives — phone tree (objectives.md v2.0 §10, new this pass). A
 * flat, single-column, low-density list — NOT a duplicate of the desktop
 * grid at a narrower width — matching HabitListScreen.tsx's exact phone
 * idiom: active section first (unlabeled), then a collapsed Archived group
 * reusing the shared CollapsibleGroup primitive (§10.2). Owns its own
 * "Back to Today" header (byte-for-byte the same wrapper HabitListScreen.tsx
 * uses, §10.1) rather than relying on App.tsx's generic back-link wrapper —
 * matching how HabitListScreen itself is routed (App.tsx passes onBack
 * directly, no external wrapper div).
 *
 * Objective management already lives on the phone today (NewObjectiveScreen/
 * EditObjectiveScreen/ObjectiveDetailScreen) — this screen is the
 * "see everything, jump anywhere, including archived" complement to the
 * existing ObjHeaderSwitcher's fast adjacent-navigation job (§2.1 reasoning,
 * phase-2d-management-history.md).
 */
export function ObjectivesScreenPhone({
  onOpenObjective,
  onCreateObjective,
  onBack,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
  readonly onCreateObjective: () => void;
  readonly onBack?: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [objectives, setObjectives] = useState<readonly ObjectiveSummaryView[] | null>(null);

  const load = useCallback(async () => {
    const list = await api.listObjectives({ includeArchived: true });
    setObjectives(list);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const active = objectives?.filter((o) => o.state === 'active') ?? [];
  const archived = objectives?.filter((o) => o.state === 'archived') ?? [];
  // §6/§10.3 inversion rule: when every objective is archived and none are
  // active, the Archived group auto-expands rather than staying collapsed
  // over the page's only content.
  const allArchivedNoneActive = (objectives?.length ?? 0) > 0 && active.length === 0;

  return (
    <main
      aria-label="Objectives"
      style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-8)', maxWidth: '480px', margin: '0 auto' }}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to Today"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--text-muted)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            minHeight: 'var(--touch-target-min)',
            marginLeft: '-8px',
          }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Today
        </button>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 'var(--space-3)',
        }}
      >
        <h1
          style={{
            fontSize: 'var(--type-heading-size)',
            fontWeight: 'var(--type-heading-weight)',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          Objectives
        </h1>
        <button
          type="button"
          onClick={onCreateObjective}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--hue-primary)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            minHeight: 'var(--touch-target-min)',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          New objective
        </button>
      </div>

      {objectives === null ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <SkeletonBlock height={56} />
          <div style={{ height: 'var(--space-3)' }} />
          <SkeletonBlock height={56} />
        </div>
      ) : objectives.length === 0 ? (
        <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-6)' }}>
          No objectives yet. Set up your first one to see it on your daily page.
        </p>
      ) : (
        <div style={{ marginTop: 'var(--space-5)' }}>
          {allArchivedNoneActive ? (
            <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              No active objectives right now.
            </p>
          ) : (
            active.map((obj, i) => (
              <ObjectiveRow
                key={obj.objectiveId}
                objective={obj}
                isLast={i === active.length - 1}
                onSelect={() => onOpenObjective(obj.objectiveId)}
              />
            ))
          )}
          {archived.length > 0 && (
            <CollapsibleGroup label={`Archived (${archived.length})`} defaultOpen={allArchivedNoneActive}>
              {archived.map((obj, i) => (
                <ObjectiveRow
                  key={obj.objectiveId}
                  objective={obj}
                  isLast={i === archived.length - 1}
                  onSelect={() => onOpenObjective(obj.objectiveId)}
                  onUndone={() => void load()}
                />
              ))}
            </CollapsibleGroup>
          )}
        </div>
      )}
    </main>
  );
}

function ObjectiveRow({
  objective,
  isLast,
  onSelect,
  onUndone,
}: {
  readonly objective: ObjectiveSummaryView;
  readonly isLast: boolean;
  readonly onSelect: () => void;
  /** Only meaningful for archived rows, which read objective.archivedEventId directly. */
  readonly onUndone?: () => void;
}) {
  const api = useDomainApi();
  const archived = objective.state === 'archived';
  const complete = objective.progress.total > 0 && objective.progress.done === objective.progress.total;
  const [undoing, setUndoing] = useState(false);

  const subtitle = archived
    ? `${objective.progress.done} of ${objective.progress.total} complete when archived`
    : objective.targetDate
      ? `${objective.progress.done} of ${objective.progress.total} · Target ${formatShortDate(objective.targetDate)}`
      : `${objective.progress.done} of ${objective.progress.total}`;

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <button
        type="button"
        onClick={onSelect}
        style={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          alignItems: 'center',
          gap: 'var(--space-3)',
          textAlign: 'left',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: 'var(--radius-sm)',
            background: archived ? 'var(--surface-hover)' : 'color-mix(in srgb, var(--hue-primary-vivid) 16%, var(--surface-raised))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Target size={16} color={archived ? 'var(--text-muted)' : 'var(--hue-primary)'} aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 500,
              color: archived ? 'var(--text-secondary)' : 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {objective.title}
          </div>
          <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
            {subtitle}
          </div>
        </div>
      </button>
      {!archived && complete && (
        <span
          style={{
            flex: 'none',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            color: 'var(--hue-primary)',
            background: 'var(--surface-hover)',
            padding: '2px 8px',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          Complete
        </span>
      )}
      {archived && objective.archivedEventId && (
        <button
          type="button"
          disabled={undoing}
          onClick={() => {
            setUndoing(true);
            void api.revertLogEntry(objective.archivedEventId as never).then(() => onUndone?.());
          }}
          style={{
            flex: 'none',
            color: 'var(--text-secondary)',
            textDecoration: 'underline',
            fontWeight: 400,
            fontSize: 'var(--type-caption-size)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-1)',
          }}
        >
          Undo
        </button>
      )}
    </div>
  );
}
